/* Copyright (c) 2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useUpdateGroupMutation, UpdateGroupInput } from "~/generated/graphql";
import { showError } from "~/services/toast";
import { getUpdateObject } from "../../hooks/useUpdateResourceGroup/utils";
import { useResourceGroup } from "../../ResourceGroupDetails/ResourceGroupProvider";
import { ResourceGroupFields } from "./types";

type Props = {
  onSuccess: () => void;
};

export function useUpdateResourceGroup({ onSuccess }: Props) {
  const { resourceGroup } = useResourceGroup();

  const [updateGroupMutation, { loading }] = useUpdateGroupMutation({
    onCompleted: (data) => {
      if (data.updateGroup) {
        onSuccess();
      }
    },
    onError: (error) => {
      showError(error.message);
    },
    update(cache, { data }, { variables }) {
      if (data?.updateGroup && variables?.input) {
        cache.modify({
          fields: {
            group: (existing) => {
              return {
                group: [existing, variables.input],
              };
            },
          },
        });

        cache.gc();
      }
    },
  });

  function updateGroup(data: ResourceGroupFields | UpdateGroupInput) {
    resourceGroup &&
      updateGroupMutation({
        variables: {
          input: {
            ...getUpdateObject(data, resourceGroup),
          },
        },
      });
  }

  return [updateGroup, loading] as const;
}
